import React, {
  ForwardRefRenderFunction,
  ReactNode,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import clsx from "clsx";

import CommonLabel from "../Label";

interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  name?: string;
  required?: boolean;
  resizable?: boolean;
  error?: string;
  infoMessage?: string;
  maxLength?: number;
  inputClassName?: string;
  ref?: React.Ref<HTMLTextAreaElement>;
  subLabel?: ReactNode;
}

const CommonTextArea: ForwardRefRenderFunction<HTMLTextAreaElement, Props> = (
  {
    label,
    name,
    className,
    required,
    resizable,
    infoMessage,
    error,
    inputClassName,
    subLabel,
    maxLength = 2000,
    ...props
  }: Props,
  ref
) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [onFocusState, setOnFocusState] = useState(false);

  useEffect(() => {
    if (resizable && textAreaRef.current) {
      textAreaRef.current.style.height = "inherit";
      const scrollHeight = textAreaRef.current.scrollHeight;
      textAreaRef.current.style.height = `${scrollHeight}px`;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]); // we can adjust height whenever value prop changes

  // TODO: 스크롤 시 textarea만 영향받도록 수정

  return (
    <div className={clsx(["flex flex-col gap-2", className])}>
      {label && (
        <CommonLabel htmlFor={name} label={label} required={required} />
      )}
      {subLabel}
      <div className="flex flex-col gap-1">
        <textarea
          ref={ref ?? textAreaRef}
          id={name}
          name={name}
          maxLength={maxLength}
          className={clsx([
            "resize-none p-4 text-base text-gray-900 border rounded-[3px] focus:outline-none min-h-[80px] h-[120px] design_min:placeholder:text-sm",
            { "border-brand-primary-magenta": error || onFocusState },
            { "border-gray-300": !onFocusState },
            inputClassName,
          ])}
          onFocus={() => setOnFocusState(true)}
          onBlur={() => setOnFocusState(false)}
          {...props}
        />
        {infoMessage && !error && (
          <div className="pl-5 text-xs text-brand-neutral-50">
            {infoMessage}
          </div>
        )}
      </div>
      {error && (
        <div
          className="px-4 pt-1 text-brand-primary-magenta text-xs"
          aria-label="errorMessage"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default React.forwardRef(CommonTextArea);
